import { getOrganizations, getSavedViewsForOrganizations, type schema } from "@repo/database";
import { isAiEnabled } from "@repo/edition";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import AdminCommand from "@/components/admin/shell/admin-command";
import { GlobalCreateTaskDialog } from "@/components/admin/shell/global-create-task-dialog";
import { NavigationTracker } from "@/components/admin/shell/navigation-tracker";
import { RootProvider } from "@/components/admin/shell/context";
import { AdminSidebar, Wrapper } from "@/components/generic/wrapper";
import { PostHogUserSync } from "@/components/post-hog-provider";
import { getAccess, requireAccount } from "@/getAccess";
import { useGlobalCommands } from "@/hooks/commands/useGlobalCommands";
import { getOgImageUrl, seo } from "@/seo";

// --- SERVER FUNCTIONS ---

// Base authentication fetcher
const fetchAuth = createServerFn({ method: "GET" }).handler(async () => {
	const { account, sessionId } = await getAccess();
	return { account, sessionId };
});

// Fetch organizations for the authenticated user
export const getUserOrganizations = createServerFn({ method: "GET" }).handler(async () => {
	try {
		// Identity comes from the session, never from client input (server fns are publicly callable).
		const account = await requireAccount();
		const organizations = await getOrganizations(account.id);
		// Each org's shared views, for the sidebar's org sections (SAY-104), grouped by org id.
		const views = await getSavedViewsForOrganizations(organizations.map((org) => org.id));
		const orgViews: Record<string, schema.savedViewType[]> = {};
		for (const view of views) {
			if (view.organizationId) (orgViews[view.organizationId] ??= []).push(view);
		}
		return { account, organizations, orgViews };
	} catch (error) {
		console.error("🚨 Error fetching organizations:", error);
		if (error && typeof error === "object" && "redirect" in error) {
			throw error;
		}
		throw redirect({ to: "/auth/login" });
	}
});

// Fetch instance-level capabilities for the current deployment
const getServerCapabilities = createServerFn({ method: "GET" }).handler(async () => {
	return { aiEnabled: isAiEnabled() };
});

// --- CLIENT-SIDE AUTH CACHE ---

const AUTH_CACHE_TTL = 30000; // 30 seconds

// Only define client-side caches (avoid SSR sharing)
const authCacheMap =
	typeof window !== "undefined" ? new Map<string, { account: schema.userType | null; timestamp: number }>() : null;

const authInFlightMap =
	typeof window !== "undefined"
		? new Map<string, Promise<{ account: schema.userType | null; sessionId?: string }>>()
		: null;

/**
 * Clears the client-side auth cache.
 * Call this after updating user data to ensure fresh data on next navigation/refresh.
 */
export function clearAuthCache(): void {
	if (authCacheMap) {
		authCacheMap.clear();
	}
	if (authInFlightMap) {
		authInFlightMap.clear();
	}
}

/**
 * Session-aware cached auth fetcher.
 * - Uses sessionId for isolation
 * - Deduplicates concurrent requests
 * - Disabled during SSR to prevent user leaks
 */
async function getCachedAuth(): Promise<{
	account: schema.userType | null;
	sessionId?: string;
}> {
	// On the server, always get fresh data
	if (typeof window === "undefined") return fetchAuth();

	const initial = await fetchAuth();
	const sessionId = initial.sessionId || initial.account?.id || "anonymous";

	const now = Date.now();
	// biome-ignore lint/style/noNonNullAssertion: <dont care>
	const cache = authCacheMap!;
	// biome-ignore lint/style/noNonNullAssertion: <dont care>
	const inFlight = authInFlightMap!;
	const cached = cache.get(sessionId);

	// Return cached result if still valid
	if (cached && now - cached.timestamp < AUTH_CACHE_TTL) {
		return { account: cached.account, sessionId };
	}

	// Deduplicate concurrent requests
	const existingInFlight = inFlight.get(sessionId);
	if (existingInFlight) return existingInFlight;

	// Fetch fresh data and update cache
	const newReq = fetchAuth()
		.then((result) => {
			if (result.account) {
				cache.set(sessionId, {
					account: result.account,
					timestamp: Date.now(),
				});
			} else {
				cache.delete(sessionId);
			}
			return result;
		})
		.finally(() => {
			inFlight.delete(sessionId);
		});

	inFlight.set(sessionId, newReq);
	return newReq;
}

// --- ROUTE CONFIGURATION ---

export const Route = createFileRoute("/(admin)")({
	head: () => ({
		meta: seo({
			title: "Admin",
			image: getOgImageUrl({ type: "simple", title: "Sayr Admin" }),
		}),
	}),

	beforeLoad: async () => {
		const { account } = await getCachedAuth();
		return { account };
	},

	loader: async ({ context }) => {
		if (!context.account) {
			throw redirect({ to: "/auth/login" });
		}

		const [orgsResult, capabilities] = await Promise.all([getUserOrganizations(), getServerCapabilities()]);

		return { ...orgsResult, aiEnabled: capabilities.aiEnabled };
	},
	component: AdminLayout,
});

// --- COMPONENT ---

function AdminLayout() {
	const { account, organizations, orgViews, aiEnabled } = Route.useLoaderData();

	return (
		<div className="flex h-dvh max-h-dvh flex-row bg-sidebar overflow-hidden">
			<RootProvider account={account} organizations={organizations} orgViews={orgViews} aiEnabled={aiEnabled}>
				<PostHogUserSync user={account ? { id: account.id, email: account.email, name: account.name } : null} />
				<NavigationTracker />
				<GlobalCommandRegistrar />
				<GlobalCreateTaskDialog />
				<AdminSidebar />
				<div className="flex flex-col flex-1 min-w-0 h-full">
					<AdminCommand />
					<Wrapper>
						<div className="relative h-full max-h-full">
							<Outlet />
						</div>
					</Wrapper>
				</div>
			</RootProvider>
		</div>
	);
}

/** Registers global commands. Must be rendered inside RootProvider. */
function GlobalCommandRegistrar() {
	useGlobalCommands();
	return null;
}
